import React, { useEffect, useState } from "react";
import { api, downloadPdf, formatMoney, formatDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import DatePicker from "@/components/DatePicker";
import ClienteAutocomplete from "@/components/ClienteAutocomplete";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Download, ReceiptText, Printer, FileX2 } from "lucide-react";
import { toast } from "sonner";

export default function Remitos() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ desde: "", hasta: "", cliente: "", cliente_id: "" });

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.desde) params.desde = filters.desde;
      if (filters.hasta) params.hasta = filters.hasta;
      if (filters.cliente_id) params.cliente_id = filters.cliente_id;
      const res = await api.get("/remitos", { params });
      setRows(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  const descargar = async (r) => {
    try {
      await downloadPdf(`/remitos/${r.id}/pdf`, `remito_${r.remito_id}.pdf`);
    } catch {
      toast.error("Error al descargar PDF.");
    }
  };

  const exportarRango = async (formato = "normal") => {
    try {
      const params = new URLSearchParams();
      if (filters.desde) params.append("desde", filters.desde);
      if (filters.hasta) params.append("hasta", filters.hasta);
      if (filters.cliente_id) params.append("cliente_id", filters.cliente_id);
      if (formato === "4up") params.append("formato", "4up");
      const url = `/remitos/export/pdf${params.toString() ? "?" + params.toString() : ""}`;
      const suffix = formato === "4up" ? "_4up" : "";
      await downloadPdf(url, `remitos_${filters.desde || "inicio"}_${filters.hasta || "hoy"}${suffix}.pdf`);
    } catch {
      toast.error("Error al exportar PDF.");
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Documentos"
        title="Remitos"
        subtitle="Cada pedido confirmado genera un remito. Descargalos individualmente o en lote."
      >
        <Button
          variant="outline"
          className="border-[#E5E0D8] hover:bg-[#F9F6F0] gap-2"
          onClick={() => exportarRango("normal")}
          data-testid="rem-export-rango"
        >
          <Printer className="h-4 w-4" /> PDF normal
        </Button>
        <Button
          className="bg-[#C85A32] hover:bg-[#A64A29] text-white gap-2"
          onClick={() => exportarRango("4up")}
          data-testid="rem-export-4up"
        >
          <Printer className="h-4 w-4" /> PDF compacto (4×hoja)
        </Button>
      </PageHeader>

      <Card className="p-4 sm:p-5 border-[#E5E0D8] rounded-2xl bg-white mb-5">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 items-end">
          <div>
            <Label className="text-[11px] text-[#595959] mb-1.5 block">Desde</Label>
            <DatePicker value={filters.desde} onChange={(v) => setFilters((f) => ({ ...f, desde: v }))} testid="rem-f-desde" />
          </div>
          <div>
            <Label className="text-[11px] text-[#595959] mb-1.5 block">Hasta</Label>
            <DatePicker value={filters.hasta} onChange={(v) => setFilters((f) => ({ ...f, hasta: v }))} testid="rem-f-hasta" />
          </div>
          <div>
            <Label className="text-[11px] text-[#595959] mb-1.5 block">Cliente</Label>
            <ClienteAutocomplete
              value={filters.cliente}
              cliente_id={filters.cliente_id}
              onChange={({ nombre, cliente_id }) => setFilters((f) => ({ ...f, cliente: nombre, cliente_id }))}
              testid="rem-f-cliente"
              placeholder="Todos"
            />
          </div>
          <Button
            variant="outline"
            className="border-[#E5E0D8] hover:bg-[#F9F6F0] h-10"
            onClick={() => setFilters({ desde: "", hasta: "", cliente: "", cliente_id: "" })}
          >
            Limpiar
          </Button>
        </div>
      </Card>

      <Card className="border-[#E5E0D8] rounded-2xl bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-[#F9F6F0] border-b border-[#E5E0D8]">
              <tr className="text-[11px] uppercase tracking-[0.14em] text-[#8C8C8C]">
                <th className="px-5 py-3">Remito</th>
                <th className="px-5 py-3">Pedido</th>
                <th className="px-5 py-3">Fecha</th>
                <th className="px-5 py-3">Cliente</th>
                <th className="px-5 py-3 text-right">Total</th>
                <th className="px-5 py-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="text-center text-[#8C8C8C] py-12">Cargando...</td></tr>}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center py-16">
                    <FileX2 className="h-10 w-10 text-[#C85A32] mx-auto mb-3 opacity-50" />
                    <div className="font-display font-semibold text-lg text-[#1A1A1A]">Sin remitos aún</div>
                    <p className="text-sm text-[#595959] mt-1">Confirmá un pedido para generar el primer remito.</p>
                  </td>
                </tr>
              )}
              {!loading && rows.map((r, i) => (
                <tr key={r.id} className="border-b border-[#F0EBE0] hover:bg-[#FBF8F2] fade-row" style={{ animationDelay: `${i * 20}ms` }} data-testid={`rem-row-${r.remito_id}`}>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-2 font-mono font-semibold text-[#C85A32]">
                      <ReceiptText className="h-4 w-4" /> {r.remito_id}
                    </div>
                  </td>
                  <td className="px-5 py-4 font-mono text-sm text-[#595959]">{r.pedido_codigo}</td>
                  <td className="px-5 py-4 text-sm">{formatDate(r.fecha)}</td>
                  <td className="px-5 py-4 text-sm font-medium">{r.cliente}</td>
                  <td className="px-5 py-4 text-right font-mono font-semibold">{formatMoney(r.total)}</td>
                  <td className="px-5 py-4 text-right">
                    <Button variant="ghost" size="sm" className="gap-1.5 text-[#595959] hover:text-[#C85A32] hover:bg-[#F4EAE6]" onClick={() => descargar(r)} data-testid={`rem-descargar-${r.remito_id}`}>
                      <Download className="h-4 w-4" /> PDF
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
